import React, { useState, useEffect, useRef } from 'react';
import './ColorAssignmentSelector.css';

/**
 * Color Assignment Selector für Space Settings
 * Ermöglicht Benutzern festzulegen, welche Farbe für welchen Zweck verwendet wird
 */
const ColorAssignmentSelector = ({ 
    colors, 
    currentAssignments, 
    onAssignmentsChange,
    showPreview = true 
}) => {
    const [colorAssignments, setColorAssignments] = useState({
        background: 'color5',      // Heller Hintergrund
        primaryText: 'color2',     // Dunkler Text
        secondaryText: 'color3',   // Mittlerer Text
        buttons: 'color1',         // Akzent für Buttons
        buttonText: 'color5',      // Button-Text-Farbe
        accents: 'color1',         // Highlights & Akzente
        ...currentAssignments
    });

    // ✅ Track previous currentAssignments to avoid unnecessary updates
    const prevAssignmentsRef = useRef(currentAssignments);

    // Update local state when currentAssignments prop changes
    useEffect(() => {
        const prevAssignmentsString = JSON.stringify(prevAssignmentsRef.current);
        const currentAssignmentsString = JSON.stringify(currentAssignments);
        
        if (currentAssignments && prevAssignmentsString !== currentAssignmentsString) {
            console.log('🔄 [ColorAssignmentSelector] Syncing with new currentAssignments:', {
                from: prevAssignmentsRef.current,
                to: currentAssignments
            });
            setColorAssignments({
                background: 'color5',
                primaryText: 'color2',
                secondaryText: 'color3',
                buttons: 'color1',
                buttonText: 'color5',
                accents: 'color1',
                ...currentAssignments
            });
            prevAssignmentsRef.current = currentAssignments;
        }
    }, [currentAssignments]); // ✅ FIXED: React to prop changes (z.B. nach Refresh)

    // Update parent component when assignments change (prevent loop)
    const updateParent = (newAssignments) => {
        if (onAssignmentsChange && JSON.stringify(newAssignments) !== JSON.stringify(colorAssignments)) {
            onAssignmentsChange(newAssignments);
        }
    };

    // Intelligente Button-Text-Farb-Logik
    const getContrastColor = (buttonColorKey, colors) => {
        if (!colors || !buttonColorKey || !colors[buttonColorKey]) return 'color5';
        
        const buttonColor = colors[buttonColorKey];
        const rgb = parseInt(buttonColor.slice(1), 16);
        const r = (rgb >> 16) & 0xff;
        const g = (rgb >>  8) & 0xff;
        const b = (rgb >>  0) & 0xff;
        const luma = 0.299 * r + 0.587 * g + 0.114 * b;
        
        // Wenn Button dunkel ist, verwende helle Schrift
        return luma < 128 ? 'color5' : 'color2';
    };

    const handleAssignmentChange = (assignmentType, colorKey) => {
        const newAssignments = { ...colorAssignments, [assignmentType]: colorKey };
        
        // Automatische Button-Text-Farb-Anpassung
        if (assignmentType === 'buttons') {
            newAssignments.buttonText = getContrastColor(colorKey, colors);
        }
        
        setColorAssignments(newAssignments);
        
        // Update parent immediately without causing loop
        if (onAssignmentsChange) {
            onAssignmentsChange(newAssignments);
        }
    };

    const assignmentDefinitions = [
        {
            key: 'background',
            label: 'Background',
            icon: 'fas fa-fill-drip',
            description: 'Main background color for widgets and sections'
        },
        {
            key: 'primaryText',
            label: 'Primary Text',
            icon: 'fas fa-font',
            description: 'Main text color for titles and headlines'
        },
        {
            key: 'secondaryText',
            label: 'Secondary Text',
            icon: 'fas fa-align-left',
            description: 'Secondary text color for descriptions and body text'
        },
        {
            key: 'buttons',
            label: 'Buttons',
            icon: 'fas fa-mouse-pointer',
            description: 'Button background color and accent elements'
        },
        {
            key: 'buttonText',
            label: 'Button Text',
            icon: 'fas fa-i-cursor',
            description: 'Text color for buttons (auto-adjusted for contrast)',
            isAutomatic: true
        },
        {
            key: 'accents',
            label: 'Accents',
            icon: 'fas fa-star',
            description: 'Accent color for highlights and special elements'
        }
    ];

    const colorOptions = colors ? Object.keys(colors) : ['color1', 'color2', 'color3', 'color4', 'color5'];

    return (
        <div className="color-assignment-selector">
            <div className="color-assignment-header">
                <h4>
                    <i className="fas fa-palette"></i>
                    Color Assignments
                </h4>
                <p>Define which colors are used for different elements in your pages</p>
            </div>

            <div className="color-assignment-main-container">
                {/* Left side: Assignments */}
                <div className="color-assignments-grid">
                    {assignmentDefinitions.map((definition) => (
                        <div key={definition.key} className="assignment-item">
                            <div className="assignment-info">
                                <label>
                                    <i className={definition.icon}></i>
                                    {definition.label}
                                    {definition.isAutomatic && (
                                        <span className="automatic-badge">Auto</span>
                                    )}
                                </label>
                                <span className="assignment-description">
                                    {definition.description}
                                </span>
                            </div>

                            <div className="assignment-selector">
                                {/* Visual Color Circles instead of dropdown */}
                                <div className="color-circles-selector">
                                    {colorOptions.map(colorKey => {
                                        const isSelected = colorAssignments[definition.key] === colorKey;
                                        const colorValue = colors?.[colorKey] || '#ccc';
                                        
                                        return (
                                            <div
                                                key={colorKey}
                                                className={`color-circle ${isSelected ? 'selected' : ''} ${definition.isAutomatic ? 'disabled' : ''}`}
                                                style={{
                                                    backgroundColor: colorValue,
                                                    cursor: definition.isAutomatic ? 'not-allowed' : 'pointer'
                                                }}
                                                onClick={() => !definition.isAutomatic && handleAssignmentChange(definition.key, colorKey)}
                                                title={`${colorKey.replace('color', 'Color ')} (${colorValue})`}
                                            >
                                                {isSelected && (
                                                    <div className="color-circle-checkmark">
                                                        <i className="fas fa-check"></i>
                                                    </div>
                                                )}
                                                <div className="color-circle-label">
                                                    {colorKey.replace('color', '')}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                
                                {/* Current Selection Info */}
                                <div className="current-selection-info">
                                    <span className="selected-color-name">
                                        {colorAssignments[definition.key].replace('color', 'Color ')}
                                    </span>
                                    <span className="selected-color-hex">
                                        {colors?.[colorAssignments[definition.key]] || '#ccc'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Right side: Live Preview */}
                {showPreview && colors && (
                    <div className="color-assignment-preview-sidebar">
                        <h5>
                            <i className="fas fa-eye"></i>
                            Live Preview
                        </h5>
                        
                        <div className="preview-container">
                            {/* Background Preview */}
                            <div 
                                className="preview-widget"
                                style={{
                                    backgroundColor: colors[colorAssignments.background],
                                    padding: '20px',
                                    borderRadius: '12px',
                                    border: `1px solid ${colors[colorAssignments.accents]}20`
                                }}
                            >
                                {/* Title */}
                                <h3 
                                    className="preview-title"
                                    style={{ 
                                        color: colors[colorAssignments.primaryText],
                                        margin: '0 0 8px 0'
                                    }}
                                >
                                    Sample Widget Title
                                </h3>
                                
                                {/* Description */}
                                <p 
                                    className="preview-description"
                                    style={{ 
                                        color: colors[colorAssignments.secondaryText],
                                        margin: '0 0 16px 0',
                                        fontSize: '14px'
                                    }}
                                >
                                    This is how your secondary text will look in widgets and descriptions.
                                </p>
                                
                                {/* Button */}
                                <button 
                                    className="preview-button"
                                    style={{
                                        backgroundColor: colors[colorAssignments.buttons],
                                        color: colors[colorAssignments.buttonText],
                                        border: 'none',
                                        padding: '10px 20px',
                                        borderRadius: '8px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Call to Action
                                </button>
                                
                                {/* Accent Element */}
                                <div 
                                    className="preview-accent"
                                    style={{
                                        marginTop: '12px',
                                        padding: '8px',
                                        backgroundColor: `${colors[colorAssignments.accents]}20`,
                                        border: `2px solid ${colors[colorAssignments.accents]}40`,
                                        borderRadius: '6px',
                                        fontSize: '12px',
                                        color: colors[colorAssignments.primaryText]
                                    }}
                                >
                                    <i className="fas fa-star" style={{ color: colors[colorAssignments.accents] }}></i>
                                    Accent element example
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ColorAssignmentSelector;
